import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { Sparkles, RefreshCw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useLatestBriefing, useRefreshBriefing } from '@/api/hooks/useHealthBriefing';

/** Minimal Markdown → elements (no dependency): headings, bullets, paragraphs; strips bold/backticks. */
function renderBriefing(md: string) {
  const clean = (s: string) => s.replace(/\*\*(.+?)\*\*/g, '$1').replace(/`/g, '');
  return md.split('\n').map((raw, i) => {
    const line = raw.trim();
    if (!line) return null;
    if (line.startsWith('## '))
      return <h4 key={i} className="mb-1 mt-3 font-semibold text-foreground">{clean(line.slice(3))}</h4>;
    if (line.startsWith('# '))
      return <h3 key={i} className="mb-1 mt-2 font-bold text-foreground">{clean(line.slice(2))}</h3>;
    if (line.startsWith('- ') || line.startsWith('* '))
      return (
        <div key={i} className="ml-1 flex gap-2">
          <span className="text-primary">•</span>
          <span>{clean(line.slice(2))}</span>
        </div>
      );
    return <p key={i} className="text-muted-foreground">{clean(line)}</p>;
  });
}

/**
 * Daily Health Briefing card — the latest AI-generated summary of what's happening and what needs
 * attention, with an on-demand refresh. Renders nothing when AI is disabled (endpoint 404s).
 */
export function HealthBriefingCard() {
  const { t } = useTranslation('ai');
  const { data: briefing, isLoading, isError } = useLatestBriefing();
  const refresh = useRefreshBriefing();

  // AI off (404) and nothing to show → hide entirely.
  if (isError && !briefing) return null;

  const generating = refresh.isPending;

  const onRefresh = () =>
    refresh.mutate(undefined, {
      onError: () => toast.error(t('briefing.error', { defaultValue: 'No se pudo generar el resumen.' })),
    });

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Sparkles className="h-4 w-4" />
          </div>
          <div>
            <CardTitle className="text-base">{t('briefing.title', { defaultValue: 'Resumen diario' })}</CardTitle>
            {briefing && (
              <CardDescription className="text-xs">
                {t('briefing.generated', { defaultValue: 'Generado' })}{' '}
                {formatDistanceToNow(new Date(briefing.generatedAt), { addSuffix: true, locale: es })}
              </CardDescription>
            )}
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onRefresh} disabled={generating} className="shrink-0">
          {generating ? (
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
          ) : (
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
          )}
          {t('briefing.refresh', { defaultValue: 'Actualizar' })}
        </Button>
      </CardHeader>
      <CardContent>
        {generating ? (
          <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('briefing.generating', { defaultValue: 'Analizando tus datos…' })}
          </div>
        ) : isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        ) : briefing ? (
          <div className="space-y-1 text-sm leading-relaxed">{renderBriefing(briefing.markdown)}</div>
        ) : (
          <div className="flex flex-col items-start gap-2 py-2">
            <p className="text-sm text-muted-foreground">
              {t('briefing.empty', { defaultValue: 'Aún no hay resumen. Genera uno con IA.' })}
            </p>
            <Button size="sm" onClick={onRefresh} disabled={generating}>
              <Sparkles className="mr-1.5 h-4 w-4" />
              {t('briefing.generate', { defaultValue: 'Generar resumen' })}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
